import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { X, ClockCounterClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import dayjs from "dayjs";

const ACTION_ICONS = { read: "👁", replied: "↩", sent: "✈", draft_created: "📝", archived: "🗄", deleted: "🗑", starred: "⭐", moved_folder: "📁", member_added: "➕", member_removed: "➖", connected: "🔌", disconnected: "⛔", connection_lost: "⚠️", created: "✨", renamed: "✏️" };

export const AuditPanel = ({ mailboxId, mailboxName, onClose }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(() => {
    api.get(`/shared-inboxes/${mailboxId}/audit`, { params: { page, date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [mailboxId, page, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="si-audit-panel" className="fixed inset-y-0 right-0 w-[420px] max-w-full bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-heading font-bold text-gray-900 flex items-center gap-2 text-sm">
          <ClockCounterClockwise size={16} className="text-blue-600" /> {t("si.audit_title", "Audit — {name}", { name: mailboxName })}
        </h3>
        <button data-testid="si-audit-close" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><X size={17} /></button>
      </div>
      <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-2">
        <input data-testid="si-audit-date-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md px-2 py-1 text-xs" />
        <span className="text-xs text-gray-400">→</span>
        <input data-testid="si-audit-date-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md px-2 py-1 text-xs" />
      </div>
      <div className="flex-1 overflow-y-auto scroll-thin px-4 py-2">
        {!data ? <p className="text-xs text-gray-400 py-4">{t("common.loading", "Loading…")}</p> : data.items.length === 0 ? (
          <p data-testid="si-audit-empty" className="text-xs text-gray-400 py-4">{t("si.audit_empty", "No activity in this period.")}</p>
        ) : data.items.map((r) => (
          <div key={r.id} data-testid="si-audit-row" className="py-2 border-b border-gray-50 text-xs">
            <div className="flex items-center gap-2">
              <span>{ACTION_ICONS[r.action] || "•"}</span>
              <span className="font-semibold text-gray-800">{r.actor_name}</span>
              <span className="text-gray-400 ml-auto">{dayjs(r.created_at).format("MMM D, HH:mm")}</span>
            </div>
            <p className="text-gray-600 mt-0.5 ml-6">{r.detail || r.action}</p>
          </div>
        ))}
      </div>
      <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[10px] text-gray-400">{t("si.audit_retention", "Audit history is kept for 30 days.")}</span>
        {data && data.pages > 1 && (
          <span className="flex items-center gap-2 text-xs">
            <button data-testid="si-audit-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-blue-600 disabled:text-gray-300">‹</button>
            {page}/{data.pages}
            <button data-testid="si-audit-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="text-blue-600 disabled:text-gray-300">›</button>
          </span>
        )}
      </div>
    </div>
  );
};
